import { BasicAuthSession, BasicAuthUserInfo } from '../basic-auth-session';
import React, { Reducer, useEffect, useReducer } from 'react';
import { SessionManager, SessionType } from '../../session-manager';
import { Button, ButtonType } from '../../../../components/button/button';
import { useHistory } from 'react-router-dom';
import './basic-auth-login-page.scss';
import i18n from '../../../i18n';
import { env_vars } from '../../../load-env';
import { Breadcrumbs } from '../../../../components/breadcrumbs/breadcrumbs';

interface FormDataState {
  userInfo: BasicAuthUserInfo;
  error: string;
}

const initFormDataState: FormDataState = {
  userInfo: {
    hunterid: '',
    birthdate: ''
  },
  error: ''
};

type Action =
    | { type: 'change'; name: string; value: string }
    | { type: 'error'; payload: string };

const reducer: Reducer<FormDataState, Action> = (state, action) => {
  switch (action.type) {
    case 'change':
      return {
        ...state,
        userInfo: {
          ...state.userInfo,
          [action.name]: action.value
        }
      };
    case 'error':
      return {
        ...state,
        error: action.payload
      };
    default:
      return state;
  }
};

export const BasicAuthLoginPage: React.FC = () => {
  const history = useHistory();
  const [formData, updateFormData] = useReducer(reducer, initFormDataState);

  // 🔥 Auto-login hvis data finnes i localStorage
  useEffect(() => {
    const init = async () => {
      try {
        const session = await SessionManager.createSession(SessionType.BASIC_AUTH);
        const userInfo = (session as BasicAuthSession).getUserInfo();

        if (userInfo) {
          await session.signin(userInfo);
          history.push('/utenlandsk/' + i18n.language);
        }
      } catch (e: any) {
        console.error(e);
      }
    };

    init();
  }, [history]);

  const validateForm = (): string => {
    if (!formData.userInfo.hunterid.match(/^(\d{6}|\d{8})$/)) {
      return i18n.t('basicAuthLogin:jegernrSkalBestataAv6Eller8Siffer');
    }

    if (!formData.userInfo.birthdate.match(/^\d{6}$/)) {
      return i18n.t('basicAuthLogin:fodselsdatoSkalBestaaAv6Siffer');
    }

    return '';
  };

  const onLogin = async (event: React.FormEvent) => {
    event.preventDefault();

    const errMsg = validateForm();
    updateFormData({ type: 'error', payload: errMsg });

    if (errMsg) return;

    try {
      await (SessionManager.session as BasicAuthSession).signin(formData.userInfo);
      history.push('/utenlandsk/' + i18n.language);
    } catch (e: any) {
      updateFormData({ type: 'error', payload: e.message });
    }
  };

  return (
      <div className="container">
        <Breadcrumbs crumbKey="lisensjegerlogin" data-tip={i18n.t('felles:login')} />

        <section className="mt-3">
          <h1>{i18n.t('felles:login')}</h1>

          <p className="intro">
            {i18n.t('basicAuthLogin:denneSiden')}
            <br />
            {i18n.t('basicAuthLogin:foraaloggeinn')}
          </p>

          <form id="login-form" onSubmit={onLogin}>
            <label htmlFor="hunterid">
              {i18n.t('basicAuthLogin:jegernummer')}{' '}
              <em>{i18n.t('basicAuthLogin:jegernummerbestaar')}</em>
            </label>

            <input
                name="hunterid"
                className="login-input"
                value={formData.userInfo.hunterid}
                onChange={(e) =>
                    updateFormData({
                      type: 'change',
                      name: e.target.name,
                      value: e.target.value
                    })
                }
                inputMode="numeric"
                maxLength={8}
            />

            <label htmlFor="birthdate">
              {i18n.t('basicAuthLogin:fodselsdato')}{' '}
              <em>{i18n.t('basicAuthLogin:tastinnfodselsdato')}</em>
            </label>

            <input
                name="birthdate"
                className="login-input"
                value={formData.userInfo.birthdate}
                onChange={(e) =>
                    updateFormData({
                      type: 'change',
                      name: e.target.name,
                      value: e.target.value
                    })
                }
                inputMode="numeric"
                maxLength={6}
            />

            {formData.error && (
                <div id="login-error" className="alert-danger login-error">{formData.error}</div>
            )}

            <Button type={ButtonType.PRIMARY} isSubmit buttonId="login-btn" onClick={() => {}}>
              {i18n.t('basicAuthLogin:login')}
            </Button>
          </form>

          <p>
            {i18n.t('basicAuthLogin:norwegian')}{' '}
            <a href={env_vars.LOGIN_NORSK_JEGER}>
              {i18n.t('basicAuthLogin:norLogin')}
            </a>
          </p>

          <a href={env_vars.BRREG_FORSIDE_URL + 'hvordan-kan-vi-hjelpe-deg/'}>
            {i18n.t('basicAuthLogin:taKontakt')}
          </a>

          {i18n.t('basicAuthLogin:hvisDuHarsporsmaal')}
        </section>
      </div>
  );
};
